import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { PackageModel, QuoteModel } from '@data-layer/models/lcl';
import { ReplaySubject } from 'rxjs';
import { AbstractControl, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { FormHelper } from '@helpers/form.helper';
import { ProductDetailsModel } from '@data-layer/models/common';
import { LclContainerService } from '@shared/lcl-mutual/services';
import { enumIncotermsType } from '@data-layer/enums/lcl/enumIncotermsType';
import { LclEditPackageComponent } from '@shared/lcl-mutual/components';
import { debounceTime, skip, take, takeUntil } from 'rxjs/operators';
import { AppService } from '@app/app.service';
import { MarineInsuranceModel } from '@data-layer/models/marine-insurance';
import { PaymentBillItemPendingModel } from '@data-layer/models/payments';
import { GenericService } from '@services/generic.service';
import { enumShippingRole } from '@data-layer/enums/user';

@Component({
  selector: 'app-lcl-edit-quote',
  templateUrl: './lcl-edit-quote.component.html',
  styleUrls: ['./lcl-edit-quote.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LclEditQuoteComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public quote: QuoteModel;
  @Input() public pendingBills: PaymentBillItemPendingModel;
  @Input() public pendingMarineInsuranceBills: PaymentBillItemPendingModel;
  @Input() public productsByCompany: ProductDetailsModel[];
  @Input() public isBillLoading: boolean;
  @Input() public isInsuranceAvailable: boolean;
  @Input() public isInsuranceLoading: boolean;
  @Input() public currentInsurance: MarineInsuranceModel;
  @Input() public marineInsuranceRequested: boolean;
  @Input() public isVoyageValidForInsurance: boolean;

  @Output() public loadBills: EventEmitter<void> = new EventEmitter<void>();
  @Output() public loadQuote: EventEmitter<void> = new EventEmitter<void>();
  @Output() public updateQuote: EventEmitter<QuoteModel> = new EventEmitter<QuoteModel>();
  @Output() public bookQuote: EventEmitter<QuoteModel> = new EventEmitter<QuoteModel>();
  @Output() public updateIncoterms: EventEmitter<{ exportLicenseRequested: boolean; incoTermsType: enumIncotermsType }> = new EventEmitter<{
    exportLicenseRequested: boolean;
    incoTermsType: enumIncotermsType;
  }>();
  @Output() public optionMarineInsuranceUpdated: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public optionLCLUpdated: EventEmitter<QuoteModel> = new EventEmitter<QuoteModel>();

  public formGroup: UntypedFormGroup;
  public packagesFormArray: UntypedFormArray;

  public recalcFormLoading: boolean;

  public isDesktop: boolean;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private fh: FormHelper,
    private lclContainerService: LclContainerService,
    private appService: AppService,
    private genericService: GenericService,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.formGroup = this.fh.createQuoteFormGroup();
    this.packagesFormArray = this.formGroup.get('packages') as UntypedFormArray;

    this.setFormData();

    this.formGroup
      .get('incoTermsType')
      .valueChanges.pipe(skip(1), debounceTime(50), takeUntil(this.destroy$))
      .subscribe((incoTermsType: enumIncotermsType) => {
        let exportLicenseRequested: boolean = this.formGroup.get('exportLicenseRequested').value;

        // #10181
        if (incoTermsType === enumIncotermsType.FOB) {
          exportLicenseRequested = false;
        }

        this.updateQuote.emit({
          ...this.quote,
          shippingRole: this.formGroup.get('shippingRole').value,
          incoTermsType,
          exportLicenseRequested,
        });
      });

    this.genericService.isDesktop$.pipe(takeUntil(this.destroy$)).subscribe((isDesktop: boolean) => {
      this.isDesktop = isDesktop;
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.quote) {
      this.setFormData();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public onSubmit(): void {
    this.bookQuote.emit(this.quote);
  }

  public onOptionISFUpdated(isChecked: boolean): void {
    this.formGroup.get('isfRequested').setValue(isChecked);

    this.optionLCLUpdated.emit({ ...this.quote, isfRequested: this.formGroup.get('isfRequested').value });
  }

  public onOptionESUpdated(isChecked: boolean): void {
    this.formGroup.get('entrySummaryRequested').setValue(isChecked);

    this.optionLCLUpdated.emit({ ...this.quote, entrySummaryRequested: this.formGroup.get('entrySummaryRequested').value });
  }

  public onOptionExportLicenseUpdated(isChecked: boolean): void {
    this.formGroup.get('exportLicenseRequested').setValue(isChecked);

    this.optionLCLUpdated.emit({ ...this.quote, exportLicenseRequested: this.formGroup.get('exportLicenseRequested').value });
  }

  public onOptionMarineInsuranceUpdated(isChecked: boolean): void {
    this.formGroup.get('marineInsuranceRequested').setValue(isChecked);

    this.optionMarineInsuranceUpdated.emit(isChecked);
  }

  public onSavePackages(packages: PackageModel[]): void {
    packages = this.lclContainerService.convertPackagesDataForUpdateSending(packages || []);

    this.updateQuote.emit({ ...this.quote, packages });
  }

  public setIncoterms(incoTermsType: number): void {
    this.formGroup.get('incoTermsType').setValue(incoTermsType);
  }

  public setShippingRole(state: enumShippingRole): void {
    this.formGroup.get('shippingRole').setValue(state);
    let newIncotermsValue: enumIncotermsType = this.quote.incoTermsType;

    switch (this.formGroup.get('incoTermsType').value) {
      case enumIncotermsType.FCA:
        newIncotermsValue = enumIncotermsType.DAT;
        break;
      case enumIncotermsType.FOB:
        newIncotermsValue = enumIncotermsType.CPT_CFR;
        break;
      case enumIncotermsType.DAT:
        newIncotermsValue = enumIncotermsType.FCA;
        break;
      case enumIncotermsType.CPT_CFR:
        newIncotermsValue = enumIncotermsType.FOB;
        break;
    }

    this.setIncoterms(newIncotermsValue);
  }

  public setFormData(): void {
    if (!!this.quote && !!this.formGroup) {
      this.packagesFormArray.clear();

      for (let i: number = 0; i < (this.quote?.packages || []).length; i++) {
        this.packagesFormArray.push(this.fh.createPackageFormGroup());
      }

      this.formGroup.patchValue(
        {
          ...this.quote,
          voyageId: this.quote?.voyage?.id,
          packages: this.lclContainerService.convertPackagesDataForForm(this.quote.packages),
        },
        { emitEvent: false },
      );

      this.packagesFormArray.controls.forEach((item: AbstractControl) => item.get('isView').setValue(true));
    }

    this.recalcFormLoading = false;
  }

  public confirmPackage(): void {
    this.recalcFormLoading = true;
    this.packagesFormArray.controls.forEach((item: AbstractControl) => item.get('isView').setValue(true));
    this.onSavePackages(
      (this.packagesFormArray.value || []).map((item: PackageModel) => ({
        ...item,
        id: !!item?.id ? item.id : undefined,
      })),
    );
  }

  public addMobilePackage(): void {
    if (this.packagesFormArray.valid) {
      const firstPackage: PackageModel = this.packagesFormArray.getRawValue()[0];
      this.packagesFormArray.push(this.fh.createPackageFormGroup(firstPackage?.lengthUnit, firstPackage?.weightUnit));
    } else {
      this.fh.markAsTouched(this.formGroup);
      this.cdr.markForCheck();
    }
  }

  public addPackage(): void {
    const componentSidebarRef: ComponentRef<LclEditPackageComponent> = this.appService.openSidebar(LclEditPackageComponent, {
      sidebarHeaderText: 'EDIT_LOAD_DETAILS',
    });

    componentSidebarRef.instance.packages = [this.fh.createPackageFormGroup().getRawValue()];
    componentSidebarRef.instance.isUpdatedLayout = true;
    componentSidebarRef.instance.isShowClear = true;
    componentSidebarRef.instance.dataSaved.pipe(take(1)).subscribe((val: PackageModel[]) => {
      this.recalcFormLoading = true;

      const result: PackageModel[] = this.lclContainerService.convertSidebarEditSingleData(
        [...this.quote.packages, ...val],
        this.quote.packages?.[0],
        val,
        this.quote.packages?.length + 1,
      );

      this.onSavePackages(result);
      this.appService.closeSidebar();
    });
  }

  public editPackage(id: number): void {
    const componentSidebarRef: ComponentRef<LclEditPackageComponent> = this.appService.openSidebar(LclEditPackageComponent, {
      sidebarHeaderText: 'EDIT_LOAD_DETAILS',
    });

    const formItem: AbstractControl = this.fh.createPackageFormGroup();
    const packageData: PackageModel = this.quote.packages.find((quotePackage: PackageModel, index: number) => index === id);
    formItem.patchValue(packageData);

    componentSidebarRef.instance.packages = [formItem.value];
    componentSidebarRef.instance.isUpdatedLayout = true;
    componentSidebarRef.instance.isShowClear = true;
    componentSidebarRef.instance.dataSaved.pipe(take(1)).subscribe((val: PackageModel[]) => {
      this.recalcFormLoading = true;

      const result: PackageModel[] = this.lclContainerService.convertSidebarEditSingleData(this.quote.packages, packageData, val, id);

      this.onSavePackages(result);
      this.appService.closeSidebar();
    });
  }

  public onRemovePackage(id: number): void {
    this.recalcFormLoading = true;

    this.updateQuote.emit({
      ...this.quote,
      packages: this.quote.packages.filter((quotePackage: PackageModel, index: number) => index !== id),
    });
  }
}
